/**
 * Deletions browser — admin tool for finding recently deleted questions
 * and restoring them via the existing revision-restore endpoint.
 *
 * Backed by GET /console/questions/deletions (cross-partition query against
 * the question-revisions container, action='delete', filtered by changedAt
 * range). Composite index (action, changedAt) makes this an index seek.
 *
 * Restore is currently disabled — the v1 restore endpoint can only restore
 * onto an existing live doc. Deleted-question recovery is a separate path
 * (would need to re-create the live doc) and is out of scope for v1.
 * Until that lands, this page is a read-only audit/recovery-report tool.
 */

import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable, type ColumnDef, formatDateShort } from '@shared-ui/components';
import { questionApi, type DeletionsListItem } from '../../api/questionApi';

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function formatWhen(iso: string): string {
  try {
    const date = new Date(iso);
    return `${formatDateShort(iso)} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  } catch { return iso; }
}

const columns: ColumnDef<DeletionsListItem>[] = [
  {
    key: 'changedAt',
    header: 'Deleted',
    width: '14%',
    render: (d) => <span className="dc-time-ago">{formatWhen(d.changedAt)}</span>,
  },
  {
    key: 'subject',
    header: 'Subject',
    width: '8%',
    render: (d) => d.subject ?? '—',
  },
  {
    key: 'questionType',
    header: 'Type',
    width: '8%',
    render: (d) => d.questionType ?? '—',
  },
  {
    key: 'questionText',
    header: 'Question text',
    render: (d) => (
      <span style={{ display: 'block', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {d.questionText ?? ''}
      </span>
    ),
  },
  {
    key: 'changedBy',
    header: 'By',
    width: '12%',
    render: (d) => <span className="dc-email">{d.changedByName ?? d.changedBy}</span>,
  },
  {
    key: 'questionId',
    header: 'Question ID',
    width: '18%',
    render: (d) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{d.questionId}</span>,
  },
];

export function Deletions() {
  const [sinceDays, setSinceDays] = useState(7);
  const [subject, setSubject] = useState<string>('');
  const [search, setSearch] = useState('');

  const since = useMemo(() => isoDaysAgo(sinceDays), [sinceDays]);

  const { data, isLoading } = useQuery({
    queryKey: ['question-deletions', since, subject],
    queryFn: () => questionApi.getDeletions({ since, ...(subject ? { subject } : {}) }),
    staleTime: 30_000,
  });

  const filteredDeletions = useMemo(() => {
    const rows = data?.deletions ?? [];
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(d =>
      d.questionId.toLowerCase().includes(q)
      || (d.questionText ?? '').toLowerCase().includes(q)
      || (d.changedByName ?? '').toLowerCase().includes(q)
      || (d.changedBy ?? '').toLowerCase().includes(q)
      || (d.subject ?? '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const toolbar = (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <select
        value={sinceDays}
        onChange={e => setSinceDays(parseInt(e.target.value, 10))}
        style={{ padding: '4px 8px', fontSize: 13 }}
      >
        <option value={1}>Last 24 hours</option>
        <option value={7}>Last 7 days</option>
        <option value={30}>Last 30 days</option>
        <option value={90}>Last 90 days</option>
      </select>
      <select
        value={subject}
        onChange={e => setSubject(e.target.value)}
        style={{ padding: '4px 8px', fontSize: 13 }}
      >
        <option value="">All subjects</option>
        <option value="maths">Maths</option>
        <option value="physics">Physics</option>
        <option value="chemistry">Chemistry</option>
        <option value="biology">Biology</option>
      </select>
    </div>
  );

  return (
    <div className="users-page" style={{ padding: 16 }}>
      <div className="page-header">
        <h1>Question Deletions</h1>
      </div>
      <p style={{ color: 'var(--text-secondary)', marginTop: 0, fontSize: 13 }}>
        Recently deleted questions, sourced from the version-history container.
        Use this to audit deletions or look up a deleted question's last known state.
      </p>

      <DataTable
        columns={columns}
        data={filteredDeletions}
        rowKey={(d) => `${d.questionId}::${d.seq}`}
        onSearch={setSearch}
        searchPlaceholder="Search text, user, ID, subject..."
        toolbar={toolbar}
        loading={isLoading}
        emptyMessage="No deletions in this window."
      />
    </div>
  );
}
